import type {CourseDocument} from '@cip/core/firebase/client';

/** The course-document fields that decide when SL results are released. */
type SlResultsCourseFields = Pick<
  CourseDocument,
  'slAppDecisionsLive' | 'slResultsDate'
>;

/**
 * The instant section-leader results are released, from `slResultsDate`.
 *
 * @param params.courseData - The course document, or undefined while it loads.
 * @return The release instant, or null when the field is absent, empty, or not
 *   a valid date.
 * @throws Never.
 */
export function getSlResultsReleaseInstant({
  courseData,
}: {
  courseData: SlResultsCourseFields | undefined;
}): Date | null {
  return parseInstant(courseData?.slResultsDate);
}

/**
 * Whether section-leader results (the admit/reject letter, the role-based
 * confetti, the home-page course card) may be shown.
 *
 * True when `slAppDecisionsLive` is set (the manual early release), or when
 * `slResultsDate` is a valid date and `nowMs` has reached it. The date is a
 * closed start: the half-open interval `[release, +inf)`, so `nowMs` equal to
 * the release instant counts as released. A missing or unparseable date with
 * no flag is not released, nor is an unknown `nowMs`.
 *
 * @param params.courseData - The course document, or undefined while it loads
 *   (treated as not released).
 * @param params.nowMs - The current time in epoch milliseconds, ideally server
 *   time; undefined (server time not yet known) is treated as not released.
 * @return True when results are released.
 * @throws Never.
 */
export function isSlResultsReleased({
  courseData,
  nowMs,
}: {
  courseData: SlResultsCourseFields | undefined;
  nowMs: number | undefined;
}): boolean {
  if (courseData?.slAppDecisionsLive === true) {
    return true;
  }
  const release = getSlResultsReleaseInstant({courseData});
  if (release === null || nowMs === undefined) {
    return false;
  }
  return nowMs >= release.getTime();
}

/**
 * Parses an ISO 8601 course-document timestamp.
 *
 * @param value - The stored string, possibly absent or empty.
 * @return The instant, or null when absent, empty, or invalid.
 * @throws Never.
 */
function parseInstant(value: string | undefined): Date | null {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  return isNaN(date.getTime()) ? null : date;
}
